/*
 * Fonte única de verdade do pagamento de uma fatura.
 *
 * fatura.pagamentos: [{ id, valor, em }]  ← novo modelo (pagamentos parciais)
 *
 * Compatível com dados antigos:
 *  - sem o array, mas com pago === true -> quitada pelo valor cheio
 *
 * Mesmo idioma de utils/pagamento.js (pedidos), de propósito: não faz sentido
 * o app ter duas formas diferentes de responder "quanto já foi pago".
 *
 * Regra do negócio: o pago NUNCA passa do valor da fatura.
 */

const numero = (valor) => parseFloat(valor) || 0;
const centavos = (valor) => Math.round(numero(valor) * 100) / 100;
// Meio centavo de folga: soma de parcelas em ponto flutuante não fecha exato.
const FOLGA = 0.005;
const VENCIMENTO_NO_FIM = "9999-12-31";

export function calcFatura(fatura) {
  const total = numero(fatura?.valor);
  const lista = Array.isArray(fatura?.pagamentos) ? fatura.pagamentos : [];
  const pago = lista.length
    ? lista.reduce((soma, p) => soma + numero(p?.valor), 0)
    : fatura?.pago ? total : 0;
  const quitada = total > 0 ? pago >= total - FOLGA : !!fatura?.pago;
  const pct = total > 0 ? Math.min(100, Math.round((pago / total) * 100)) : quitada ? 100 : 0;
  return { total, pago: Math.min(pago, total), saldo: Math.max(0, centavos(total - pago)), quitada, pct };
}

export const faturaQuitada = (fatura) => calcFatura(fatura).quitada;
export const saldoFatura = (fatura) => calcFatura(fatura).saldo;
export const somarSaldos = (faturas) => centavos(faturas.reduce((soma, f) => soma + saldoFatura(f), 0));
export const somarPagos = (faturas) => centavos(faturas.reduce((soma, f) => soma + calcFatura(f).pago, 0));

/*
 * Distribui um valor entre as faturas em aberto, da mais antiga para a mais
 * nova — é como banco e cartão amortizam: o que venceu primeiro sai primeiro.
 * Fatura sem vencimento fica por último.
 *
 * Só calcula: devolve o que precisa ser gravado, sem tocar no banco. "sobra" é
 * o que não coube em dívida nenhuma (pagou mais do que devia).
 */
export function distribuirPagamento(faturas, valor) {
  let restante = centavos(valor);
  if (restante <= 0) return { alocacoes: [], usado: 0, sobra: 0 };

  const abertas = (faturas || [])
    .map((fatura) => ({ fatura, saldo: saldoFatura(fatura) }))
    .filter((item) => item.saldo > FOLGA)
    .sort((a, b) => (a.fatura.vencimento || VENCIMENTO_NO_FIM).localeCompare(b.fatura.vencimento || VENCIMENTO_NO_FIM));

  const alocacoes = [];
  for (const { fatura, saldo } of abertas) {
    if (restante <= FOLGA) break;
    const valorPago = centavos(Math.min(saldo, restante));
    alocacoes.push({ fatura, valor: valorPago, quitada: valorPago >= saldo - FOLGA });
    restante = centavos(restante - valorPago);
  }

  return { alocacoes, usado: centavos(alocacoes.reduce((soma, a) => soma + a.valor, 0)), sobra: Math.max(0, restante) };
}

/*
 * Parcelas de uma compra: os centavos que sobram da divisão vão na última
 * (100 em 3x = 33,33 + 33,33 + 33,34) e o vencimento não pula de mês
 * (31/01 + 1 mês = 28/02, não 03/03). dataPrimeira: "AAAA-MM-DD".
 */
export function gerarParcelas(valorTotal, quantidade, dataPrimeira) {
  const n = Math.max(1, parseInt(quantidade, 10) || 1);
  const totalCentavos = Math.round(numero(valorTotal) * 100);
  const parte = Math.floor(totalCentavos / n);
  const [ano, mes, dia] = String(dataPrimeira || "").split("-").map(Number);
  if (!ano || !mes || !dia) return [];
  return Array.from({ length: n }, (_, i) => {
    const ultimoDia = new Date(Date.UTC(ano, mes + i, 0)).getUTCDate();
    const vencimento = new Date(Date.UTC(ano, mes - 1 + i, Math.min(dia, ultimoDia))).toISOString().slice(0, 10);
    return { num: i + 1, valor: (i === n - 1 ? totalCentavos - parte * (n - 1) : parte) / 100, vencimento };
  });
}
